import { all, call, put, takeLatest, select } from "redux-saga/effects";

import {
  search as searchApi,
  prospect as prospectApi,
  decisions as decisionsAPIClient,
  search
} from "../../api/apiClient";

import {
  getAuthorizationHeader,
  getSignatoryModel,
  getOrganizationInfoModel,
  getAppConfig
} from "../selectors/appConfig";
import { getProspectOverview, getSearchResults } from "../selectors/searchProspect";
import { getSelectedStakeholderId } from "../selectors/stakeholders";
import { getwtmSessionDetails } from "../selectors/webToMobile";
import { checkLoginStatus } from "../selectors/loginSelector";
import { getOtp } from "../selectors/otp";
import { setIsMultipleSignatoriesJourney, updateStakeholdersIds } from "../actions/stakeholders";

import { setConfig, loadMetaData, updateProspect } from "../actions/appConfig";
import { termsAndConditionsAccepted } from "../actions/termsAndConditions";
import { setDecisions } from "../actions/decisions";
import {
  retrieveApplicantInfoSuccess,
  getProspectInfoSuccess,
  getProspectInfoFail,
  RETRIEVE_APPLICANT_INFO,
  GET_PROSPECT_INFO_REQUEST
} from "../actions/retrieveApplicantInfo";
import { setRakezData, setRakezFetchDataProcessCompleted } from "../actions/rakezData";

import { COMPANY_STAKEHOLDER_ID } from "../../containers/CompanyStakeholders/constants";
import { COMPANY_INFO_PAGE_ID } from "../../containers/CompanyInfo/constants";
import { SELECT_SERVICES_PAGE_ID } from "../../containers/SelectServices/constants";
import { OUTSIDE_BASE_PATH } from "../../containers/Additional/constants";
import { signatoryCompanyInfo, kycAnnexure } from "../../constants/prospectPatches";
import {
  VIEW_IDS,
  COMPANY_SIGNATORY_ID,
  FINAL_QUESTIONS_COMPANY_ID,
  DEFAULT_SIGNATORY_ID
} from "../../constants";

import { postLoginToken } from "./appConfig";

import { log } from "../../utils/loggger";

const CUSTOMER_EDIT_STATUSES = ["PEN_SUBMITTED", "EFR_SUBMITTED", "INFO_REQUIRED", "VER_PENDING"];

export function* retrieveApplicantInfoSaga({ payload }) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const inputParam = {
      applicantName: payload.fullName || "",
      countryCode: payload.countryCode || "",
      mobileNo: payload.mobileNo || "",
      leadNumber: payload.leadNumber || "",
      tradeLicenseNo: payload.tradeLicenseNo || "",
      email: payload.email || "",
      eidNumber: ""
    };

    const response = yield call(searchApi.searchApplication, inputParam, headers);
    yield put(retrieveApplicantInfoSuccess(response.data));
  } catch (error) {
    log(error);
  }
}

const concatAddressInfo = (configAddress, existAddress) => {
  return configAddress.map((address, addressIndex) => {
    if (address.officeAddressDifferent) {
      return {
        ...address,
        addressDetails: existAddress[addressIndex]
          ? existAddress[addressIndex].addressDetails
          : address.addressDetails,
        officeAddressDifferent:
          existAddress[addressIndex] && existAddress[addressIndex].officeAddressDifferent
            ? existAddress[addressIndex].officeAddressDifferent
            : address.officeAddressDifferent
      };
    } else {
      return {
        ...address,
        addressDetails: existAddress[addressIndex]
          ? existAddress[addressIndex].addressDetails
          : address.addressDetails
      };
    }
  });
};

function removeNullUndefinedKeys(obj, initialProspect) {
  let newObj = JSON.parse(JSON.stringify(obj));
  for (let key in newObj) {
    if (newObj[key] === null || newObj[key] === undefined) {
      newObj[key] = initialProspect[key];
    }
  }
  return newObj;
}

export function* getProspectIdInfo({ payload }) {
  try {
    const postTokenRequired = payload.postToken || false;
    const isAgentJourney = yield select(checkLoginStatus);
    const {
      verifiedCredential: { email, mobileNo, sid }
    } = yield select(getOtp);
    const selectedSignatoryId = yield select(getSelectedStakeholderId);
    const selectedSignatoryIdIfNotFound = selectedSignatoryId || sid || DEFAULT_SIGNATORY_ID;

    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);
    const isWtm = !!webMobileRefId;

    if (postTokenRequired) {
      yield call(postLoginToken, payload.prospectId, selectedSignatoryId);
    }

    const authHeader = yield select(getAuthorizationHeader);
    const checkIfProspectExistsInStore = yield select(getProspectOverview);
    let searchResults = yield select(getSearchResults);

    // Call search API if searchResults is empty or doesn't exist
    if (!isWtm && (!searchResults || searchResults.length === 0)) {
      const headers = {
        headers: {
          ...authHeader.headers
        }
      };
      const response = yield call(search.searchApplication, { email, mobileNo }, headers);
      searchResults = response?.data?.searchResult || [];
    }

    const currentSearchItem = (searchResults || []).find(
      item => item.prospectId === payload.prospectId
    );
    const isMultipartnerPersona = currentSearchItem?.prospectVersion === "v3";
    const headers = {
      headers: {
        ...authHeader.headers,
        // The "signatory-id" header is required to identify the selected signatory.
        ...(selectedSignatoryId &&
          isMultipartnerPersona && {
            "signatory-id": selectedSignatoryId
          })
      }
    };
    const organizationInfoModel = yield select(getOrganizationInfoModel);
    const initialConfig = yield select(getAppConfig);
    const initialProspect = initialConfig.prospect;

    const applicationStatus = currentSearchItem?.status?.statusType;
    let signatoryStatus;
    if (isMultipartnerPersona && currentSearchItem?.signatoryInfo?.length > 0) {
      const partnerInfo = currentSearchItem?.signatoryInfo?.find(
        signatory => signatory.signatoryId === selectedSignatoryIdIfNotFound
      );
      signatoryStatus = partnerInfo?.status?.statusType;
    }
    if (
      !(isWtm || isAgentJourney) &&
      !CUSTOMER_EDIT_STATUSES.includes(signatoryStatus) &&
      !CUSTOMER_EDIT_STATUSES.includes(applicationStatus)
    ) {
      yield put(getProspectInfoFail(null));
      return;
    }

    let response = {};
    if (checkIfProspectExistsInStore?.generalInfo?.prospectId === payload.prospectId) {
      response["data"] = checkIfProspectExistsInStore;
    } else {
      response = yield call(
        prospectApi.get,
        payload.prospectId,
        headers,
        isWtm,
        payload.tlNo,
        payload.tlAuth
      );
    }
    const config = {
      prospect: removeNullUndefinedKeys(response.data, initialProspect),
      showCompanyDocUpload: response.data.showCompanyDocUpload || {
        showTlDocUpload: false,
        showMoaDocUpload: false,
        showAddendumDocUpload: false
      }
    };

    const tlAuthorityInfo =
      (response.data.tlAuthorityInfo && JSON.parse(response.data.tlAuthorityInfo)) || {};
    const tlAuthorityData = tlAuthorityInfo?.data || {};
    const organizationInfoData = response.data.organizationInfo || {};
    const tlFetchConsentInfo = organizationInfoData?.tlFetchConsent;
    const isRakez = organizationInfoData?.licenseIssuingAuthority?.toLowerCase() === "rake";
    const isTlAuthorityEmptyOrError =
      response.data.tlAuthorityInfo !== "" ||
      (response.data.tlAuthorityInfo !== null &&
        (!tlAuthorityData?.providerStatus || (tlAuthorityInfo?.errors?.length ?? 0) > 0));

    const isRakezFetchSuccess =
      tlAuthorityData?.providerStatus === "DATA_AVAILABLE" ||
      (isRakez && tlFetchConsentInfo && isTlAuthorityEmptyOrError);

    yield put(setRakezData(tlAuthorityData, tlAuthorityInfo));
    yield put(setRakezFetchDataProcessCompleted(isRakezFetchSuccess));
    const prospectId = response.data.generalInfo.prospectId;
    const signtoryIndex = response.data.signatoryInfo?.findIndex(
      signatory => signatory.signatoryId === selectedSignatoryIdIfNotFound
    );
    const isMultipleSignatoriesJourney =
      response.data?.signatoryInfo?.filter(signatory => signatory?.kycDetails?.isSignatory).length >
      1;

    yield put(setIsMultipleSignatoriesJourney(isMultipleSignatoriesJourney));

    if (
      config.prospect?.organizationInfo?.addressInfo &&
      !config.prospect?.organizationInfo?.addressInfo[0]?.typeOfAddress
    ) {
      config.prospect.organizationInfo.addressInfo = concatAddressInfo(
        organizationInfoModel.addressInfo,
        config.prospect.organizationInfo.addressInfo
      );
    }
    if (!response.data?.applicationInfo) {
      let isIslamicBanking = response.data?.applicationInfo?.isIslamicBanking;
      if (isIslamicBanking === null || isIslamicBanking === undefined) {
        config.prospect.applicationInfo.islamicBanking = response.data?.applicantInfo?.isIslamic;
      } else {
        config.prospect.applicationInfo.islamicBanking = isIslamicBanking;
      }
      config.prospect.applicationInfo.accountType = response.data?.applicantInfo?.productType;
    }

    const freeFieldsInfo = config.prospect?.freeFieldsInfo;
    const newStakeholder = yield select(getSignatoryModel);
    if (
      !config.prospect?.signatoryInfo?.length &&
      config.prospect?.applicationInfo?.viewId?.includes(VIEW_IDS.StakeholdersInfo)
    ) {
      config.prospect.signatoryInfo = [newStakeholder];
    }

    //ro-assist-brd1-5
    let prospect = {};
    //set signatory edited name
    try {
      config.prospect.applicationInfo.viewId = config.prospect.viewId;
      prospect["applicationInfo.viewId"] = config.prospect.viewId;
      if (config.prospect?.signatoryInfo && config.prospect?.signatoryInfo[signtoryIndex]) {
        const signatoryDetails =
          config.prospect?.signatoryInfo && config.prospect?.signatoryInfo[signtoryIndex];
        signatoryDetails["editedFullName"] =
          signatoryDetails.editedFullName || signatoryDetails.fullName;
        prospect[`signatoryInfo[${signtoryIndex}].editedFullName`] =
          signatoryDetails.editedFullName || signatoryDetails.fullName;

        //consent screen
        if (signatoryDetails.consentInfo) {
          yield put(
            termsAndConditionsAccepted({
              kfs: signatoryDetails.consentInfo?.kfsConsent?.accept,
              authorisation: signatoryDetails.consentInfo?.aecbConsent?.accept,
              generalTCs: signatoryDetails.consentInfo?.otherTncConsent?.accept
            })
          );
        }
      }
    } catch (e) {
      log(e);
    }
    //to incooperate industry mapping
    try {
      if (config.prospect?.organizationInfo) {
        prospect["organizationInfo.industryMultiSelect"] = [
          {
            industry: config.prospect?.organizationInfo.industry,
            subCategory: config.prospect?.organizationInfo.subCategory
          }
        ];
        config.prospect.organizationInfo["industryMultiSelect"] = [
          {
            industry: config.prospect?.organizationInfo.industry,
            subCategory: config.prospect?.organizationInfo.subCategory
          }
        ];
      }
    } catch (e) {
      log(e);
    }
    try {
      prospect[`${OUTSIDE_BASE_PATH}.isSameAsRegisteredAddress`] =
        config.prospect.organizationInfo?.addressInfo[0]?.officeAddressDifferent === "No";
      if (
        config.prospect.organizationInfo?.addressInfo[0]?.addressDetails[0].preferredAddress ===
        "Yes"
      ) {
        prospect[`${OUTSIDE_BASE_PATH}.preferredMailingAddrs`] = true;
      } else if (
        config.prospect.organizationInfo?.addressInfo[1].addressDetails[0]?.preferredAddress ===
        "Yes"
      ) {
        prospect[`${OUTSIDE_BASE_PATH}.preferredMailingAddrs`] = false;
      } else {
        prospect[`${OUTSIDE_BASE_PATH}.preferredMailingAddrs`] = "";
      }
      config.prospect?.signatoryInfo &&
        config.prospect.signatoryInfo.forEach((element, index) => {
          if (
            element?.addressInfo &&
            element?.addressInfo[0]?.addressDetails[0]?.preferredAddress === "Yes"
          ) {
            prospect[`signatoryInfo[${index}].signoPreferredMailingAddrs`] = true;
          } else if (
            element?.addressInfo[1] &&
            element?.addressInfo[1].addressDetails[0].preferredAddress === "Yes"
          ) {
            prospect[`signatoryInfo[${index}].signoPreferredMailingAddrs`] = false;
          } else {
            prospect[`signatoryInfo[${index}].signoPreferredMailingAddrs`] = "";
          }
          if (
            newStakeholder.addressInfo.length >
            config.prospect.signatoryInfo[index].addressInfo.length
          ) {
            config.prospect.signatoryInfo[index].addressInfo = concatAddressInfo(
              newStakeholder.addressInfo,
              config.prospect.signatoryInfo[index].addressInfo
            );
          }
          if (!element.kycDetails?.isShareholderACompany) {
            config.prospect.signatoryInfo[index].kycDetails.isShareholderACompany = false;
          }
          if (!element?.signatoryCompanyInfo) {
            config.prospect.signatoryInfo[index].signatoryCompanyInfo = signatoryCompanyInfo;
          }
        });
      if (!config.prospect?.kycAnnexure) {
        config.prospect.kycAnnexure = kycAnnexure;
      }
    } catch (error) {
      prospect[`${OUTSIDE_BASE_PATH}.isSameAsRegisteredAddress`] = false;
      prospect[`${OUTSIDE_BASE_PATH}.preferredMailingAddrs`] = "";
      config.prospect?.signatoryInfo &&
        config.prospect.signatoryInfo.forEach((element, index) => {
          prospect[`signatoryInfo[${index}].signoPreferredMailingAddrs`] = "";
        });
      log(error);
    }
    //ro-assist-brd1-5
    yield put(updateProspect(prospect));

    yield put(setConfig(config));

    if (freeFieldsInfo) {
      yield put(loadMetaData(freeFieldsInfo));
      if (freeFieldsInfo.freeField5) {
        try {
          const { completedSteps = [] } = JSON.parse(freeFieldsInfo.freeField5);

          let stakeholderStep = 0;
          let signatoryStep = 0;

          let newSteps = [];

          completedSteps.forEach(step => {
            step.flowId.startsWith(COMPANY_STAKEHOLDER_ID) &&
              (stakeholderStep = stakeholderStep + 1);

            step.flowId.startsWith(COMPANY_SIGNATORY_ID) && (signatoryStep = signatoryStep + 1);

            step.flowId.includes(COMPANY_INFO_PAGE_ID) && newSteps.push(step);

            step.flowId.includes(FINAL_QUESTIONS_COMPANY_ID) && newSteps.push(step);

            step.flowId.includes(SELECT_SERVICES_PAGE_ID) && newSteps.push(step);

            if (stakeholderStep < config.prospect.signatoryInfo.length * 6 + 1) {
              step.flowId.startsWith(COMPANY_STAKEHOLDER_ID) && newSteps.push(step);
            }

            if (signatoryStep < config.prospect.signatoryInfo.length * 4 + 1) {
              step.flowId.startsWith(COMPANY_SIGNATORY_ID) && newSteps.push(step);
            }
          });

          freeFieldsInfo.freeField5 = JSON.stringify({ completedSteps: newSteps });

          yield put(loadMetaData(freeFieldsInfo));

          const stakeholdersIds = [
            ...newSteps.reduce((acc, { flowId }) => {
              if (flowId && flowId.startsWith(COMPANY_STAKEHOLDER_ID)) {
                acc.add(flowId.split(COMPANY_STAKEHOLDER_ID)[1]);
              }
              return acc;
            }, new Set())
          ];
          yield put(updateStakeholdersIds(stakeholdersIds));
        } catch (error) {
          log(error);
        }
      }
    }
    const updatedHeaders = yield select(getAuthorizationHeader);
    const decisionValue = yield call(decisionsAPIClient.get, prospectId, updatedHeaders, isWtm);
    yield put(setDecisions(decisionValue));
    yield put(getProspectInfoSuccess(config.prospect));
  } catch (error) {
    log(error);
    yield put(getProspectInfoFail());
  }
}

export default function* retrieveApplicantSaga() {
  yield all([
    takeLatest(RETRIEVE_APPLICANT_INFO, retrieveApplicantInfoSaga),
    takeLatest(GET_PROSPECT_INFO_REQUEST, getProspectIdInfo)
  ]);
}
